// A confirmed configurator order's workbooks (orders/orderFile.ts in the worker files the order on the boat's
// Order Sheet row and order file record): the order workbook (ORDER, BY BOX, SAP) and the boat's BOM, made here
// from the row the worker wrote, exactly as the order file page makes them, and kept in Info / Files beside the
// order file (POST /api/order-files/store names and places them). The BOM is the model's STD lists sheet with
// the sections the boat hasn't got deleted, by the BOM lists page's rules and the boat's own hand ticks. Storing
// the order workbook clears the last revision's BOM, so a BOM that can't be made again never lingers.

import { bomLines, orderBomName, orderFileName, orderKeySap, orderListFolder, resolveBom, type ConfiguredOrderFiling } from '@technohull/shared';
import { api } from '../lib/api.js';
import { bomSeasonFor, fetchBomSheet } from './bom.js';
import type { BomConfig, BomList, BomListMeta } from '@technohull/shared';

export interface StoredOrderFiles {
  /** where the order workbook went */
  key: string;
  /** where its BOM went, when one could be made */
  bomKey: string;
  /** why there's no BOM ("No BOM list for the 40 yet") */
  bomWhy: string;
  /** the Info / Files folder they are in, as people read it */
  folder: string;
}

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** make and keep the order workbook and its BOM for a filed order; `stamp` says which revision it is */
export async function storeOrderFiles(f: ConfiguredOrderFiling, stamp: string): Promise<StoredOrderFiles> {
  const overview = await api.get<{ lists: BomListMeta[]; config: BomConfig }>('/api/bom');
  const season = bomSeasonFor(overview.lists, f.model, f.season);
  const list = season
    ? await api.get<{ list: BomList }>(`/api/bom/list?season=${encodeURIComponent(season)}&model=${encodeURIComponent(f.model)}`).then((r) => r.list)
    : null;
  const resolved = list ? resolveBom(list, f.values, overview.config.links[f.model] ?? {}, f.picks) : [];
  const { buildOrderWorkbook } = await import('./orderFileExport.js');
  const sapShown = orderKeySap(f.sap);
  const order = await buildOrderWorkbook({ sap: sapShown, name: f.name, model: f.model, values: f.values, lines: bomLines(resolved), stamp });
  const store = (kind: 'order' | 'bom', body: Blob) =>
    api.upload<{ ok: true; key: string }>(`/api/order-files/store?sap=${encodeURIComponent(f.sap)}&kind=${kind}`, body, XLSX_TYPE).then((r) => r.key);
  const key = await store('order', order);

  let bomKey = '';
  let bomWhy = '';
  if (!list) bomWhy = `There’s no BOM list for the ${f.model} yet: upload the STD lists on the BOM lists page.`;
  else if (resolved.some((r) => !r.section.rows)) bomWhy = 'The BOM list was uploaded before BOMs were made from the STD lists sheet: upload the STD lists again.';
  else {
    try {
      const [source, { rowsToDelete, stdSheetWorkbook }] = await Promise.all([fetchBomSheet(list.season, f.model), import('./stdSheet.js')]);
      const deleteRows = rowsToDelete(resolved.map((r) => ({ rows: r.section.rows, groupRows: r.section.groupRows, included: r.included })));
      bomKey = await store('bom', new Blob([stdSheetWorkbook(source, { deleteRows })], { type: XLSX_TYPE }));
    } catch (e) {
      bomWhy = e instanceof Error ? e.message : 'The BOM couldn’t be made.';
    }
  }
  const folder = orderListFolder(f.season, f.model).replace(/^order-lists\//, '').replace(/\/$/, '').split('/').join(' › ');
  return { key, bomKey, bomWhy, folder };
}

/** the file names a filed order's workbooks get */
export const orderFileNames = (f: Pick<ConfiguredOrderFiling, 'sap' | 'name'>) => {
  const name = orderFileName(orderKeySap(f.sap), f.name);
  return { order: name, bom: orderBomName(name) };
};
